import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import EnhancedHeader from '../components/EnhancedHeader';
import '../styles/TermsOfService.css';

const TermsOfService = () => {
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service | Active Care Residential</title>
        <meta name="description" content="Terms of Service for Active Care Residential. Understanding our service terms and conditions." />
        <meta name="keywords" content="terms of service, terms and conditions, agreement" />
      </Helmet>

      {/* Enhanced Header */}
      <EnhancedHeader
        title="Terms of Service"
        subtitle="Understanding our service agreement"
        backgroundImage="/images/residential-care-banner.jpg"
        backgroundGradient="linear-gradient(135deg, rgba(44, 62, 80, 0.8) 0%, rgba(52, 73, 94, 0.8) 100%)"
      />

      <motion.section
        className="terms-page-wrapper"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="terms-page-section">
          <div className="terms-page-container">
            <div className="terms-page-body">
              <section className="terms-page-item">
                <h2>1. Acceptance of Terms</h2>
                <p>
                  By accessing and using this website and our services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>2. Service Description</h2>
                <p>
                  Active Care Residential (Registration Number: 14464980) provides supported residential accommodation services for vulnerable adults aged 18-65. Our services include 24/7 care support, therapeutic services, and personalized care plans designed to meet individual needs.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>3. Eligibility</h2>
                <p>
                  You must be at least 18 years of age to access our services. By using our services, you represent and warrant that you meet this requirement and are legally capable of entering into a binding agreement.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>4. User Responsibilities</h2>
                <p>You agree to:</p>
                <ul className="terms-page-list">
                  <li>Provide accurate and complete information</li>
                  <li>Maintain the confidentiality of any passwords or account information</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Respect the rights of other residents and staff</li>
                  <li>Participate in care planning and treatment as agreed</li>
                  <li>Inform us of any changes in your circumstances</li>
                </ul>
              </section>

              <section className="terms-page-item">
                <h2>5. Service Agreement</h2>
                <p>
                  Upon accepting our services, you will enter into a formal Service Agreement with Active Care Residential. This agreement will specify your individual care needs, service fees, payment terms, and house rules. A copy of this agreement will be provided to you.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>6. Fees and Payments</h2>
                <p>
                  Service fees are agreed upon during the assessment process and will be specified in your Service Agreement. Fees typically range from £2,500 to £5,000 per week, depending on your care requirements. Payment terms and methods will be agreed in advance.
                </p>
                <p>
                  <strong>Late Payment:</strong> If payment is not received by the due date, we reserve the right to suspend services after providing 7 days' written notice.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>7. Care Standards</h2>
                <p>
                  All our staff are trained in safeguarding, behaviour management, and trauma-informed care. We aim to provide high-quality care that meets or exceeds regulatory standards. We are regulated by the Care Quality Commission (CQC) and work towards achieving a "Good" or "Outstanding" rating.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>8. Termination of Services</h2>
                <p>
                  Either party may terminate the service agreement with 28 days' written notice. In cases of safeguarding concerns or serious breaches of house rules, we reserve the right to terminate services with immediate effect while ensuring appropriate alternative arrangements are made.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>9. Complaints and Grievances</h2>
                <p>
                  We are committed to resolving any concerns or complaints promptly. Our complaints procedure is available upon request. All complaints will be acknowledged within 5 working days and investigated thoroughly.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>10. Liability Limitation</h2>
                <p>
                  Active Care Residential will not be liable for any indirect, incidental, special, consequential, or punitive damages arising from or related to your use of our services. Our total liability is limited to the amount of fees paid in the preceding 12 months.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>11. Website Use</h2>
                <p>
                  Our website is provided "as is" without warranties of any kind. We do not warrant that the website will be uninterrupted or error-free. We reserve the right to modify, suspend, or discontinue the website at any time without notice.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>12. Intellectual Property</h2>
                <p>
                  All content on our website, including text, graphics, logos, and images, is the property of Active Care Residential or its content suppliers and is protected by international copyright laws.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>13. Prohibited Activities</h2>
                <p>You may not:</p>
                <ul className="terms-page-list">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Harass, threaten, or defame other residents or staff</li>
                  <li>Engage in abusive behavior toward staff or other residents</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with other residents' use of services</li>
                  <li>Distribute or transmit harmful or illegal content</li>
                </ul>
              </section>

              <section className="terms-page-item">
                <h2>14. Governing Law</h2>
                <p>
                  These Terms of Service are governed by and construed in accordance with the laws of England and Wales, and you irrevocably submit to the exclusive jurisdiction of the courts located in England.
                </p>
              </section>

              <section className="terms-page-item">
                <h2>15. Contact Us</h2>
                <p>
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <div className="terms-page-contact-box">
                  <p><strong>Active Care Residential</strong></p>
                  <p>Registration Number: 14464980</p>
                  <p>Email: info@activecareresidential.co.uk</p>
                  <p>Phone: +44 7737 207521</p>
                  <p>Address: East Ham, London</p>
                </div>
              </section>

              <section className="terms-page-item">
                <h2>16. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to the website. Your continued use of our services constitutes acceptance of the revised Terms of Service.
                </p>
              </section>
            </div>
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default TermsOfService;